import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import { Button, IconButton } from "@mui/material";
import {
  apiMaintenanceOfCertificationUrl,
  apiResourceGuideUrl,
  healthcareApiDocsPdfFileName,
  healthcareApiDocsPdfUrl,
  serviceBaseUrls as defaultServiceBaseUrls,
  standards as defaultStandards,
  technologies as defaultTechnologies,
} from "./constants";

type ServiceBaseUrlItem = {
  label: string;
  href: string;
  displayUrl: string;
};

type DeveloperGuideMetadataProps = {
  title?: string;
  version?: string;
  lastUpdated?: string;
  standards?: string[];
  technologies?: string[];
  serviceBaseUrls?: readonly ServiceBaseUrlItem[];
  pdfUrl?: string;
  pdfFileName?: string;
  onDownloadPdf?: () => void;
};

const DeveloperGuideMetadata = ({
  title = "Healthcare API Developer Guide",
  version = "11",
  lastUpdated = "July 22, 2026",
  standards = defaultStandards,
  technologies = defaultTechnologies,
  serviceBaseUrls = defaultServiceBaseUrls,
  pdfUrl = healthcareApiDocsPdfUrl,
  pdfFileName = healthcareApiDocsPdfFileName,
  onDownloadPdf,
}: DeveloperGuideMetadataProps) => {
  const handleViewDocumentation = () => {
    window.open(pdfUrl, "_blank", "noopener,noreferrer");
  };

  const handleDownloadPdf = () => {
    if (onDownloadPdf) {
      onDownloadPdf();
      return;
    }

    const link = document.createElement("a");
    link.href = pdfUrl;
    link.download = pdfFileName;
    link.rel = "noopener noreferrer";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <section className="relative overflow-hidden rounded-2xl border border-[#e3e7f3] bg-white p-5 shadow-[0_4px_12px_rgba(4,30,121,0.04)] sm:rounded-[28px] sm:p-6 md:p-7">
      <div className="flex flex-col gap-3 border-b border-[#e3e7f3] pb-4 sm:gap-4 lg:flex-row lg:items-start lg:justify-between lg:gap-6">
        <div className="flex min-w-0 flex-1 items-start gap-2.5 sm:gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#e5ebff] text-(--darkblue) sm:h-11 sm:w-11 [&_.MuiSvgIcon-root]:text-xl sm:[&_.MuiSvgIcon-root]:text-2xl">
            <MenuBookOutlinedIcon />
          </div>
          <div className="min-w-0 flex-1">
            <p className="m-0 text-[10px] font-semibold uppercase tracking-widest text-(--desaturatedbluecustom) sm:text-xs">
              Guide Details
            </p>
            <h2 className="m-0 mb-2 text-xl font-semibold leading-tight sm:text-2xl">
              {title}
            </h2>
            <div className="flex flex-wrap gap-1.5 sm:gap-2">
              {standards.map((item) => (
                <span
                  key={item}
                  className="inline-flex max-w-full rounded-full border border-[#e3e7f3] bg-[#e5ebff] px-2.5 py-1 text-[11px] font-semibold sm:px-3 sm:text-xs"
                >
                  {item}
                </span>
              ))}
            </div>
          </div>
        </div>

        <div className="grid w-full grid-cols-2 gap-2 sm:gap-2.5 lg:w-auto lg:shrink-0 lg:justify-end">
          <div className="min-w-0 rounded-xl border border-[#e3e7f3] bg-[#f7faff] px-2.5 py-2 sm:px-3">
            <p className="m-0 text-[10px] font-semibold tracking-widest text-(--desaturatedbluecustom) sm:text-xs">
              Version
            </p>
            <p className="m-0 mt-0.5 text-sm font-semibold sm:text-base">
              {version}
            </p>
          </div>
          <div className="min-w-0 rounded-xl border border-[#e3e7f3] bg-[#f7faff] px-2.5 py-2 sm:px-3">
            <p className="m-0 text-[10px] font-semibold tracking-widest text-(--desaturatedbluecustom) sm:text-xs">
              Last Updated
            </p>
            <p className="m-0 mt-0.5 text-sm font-semibold sm:text-base">
              {lastUpdated}
            </p>
          </div>
        </div>
      </div>

      <div className="mt-5">
        <p className="m-0 mb-2.5 text-[10px] font-semibold uppercase tracking-widest text-(--desaturatedbluecustom) sm:text-xs">
          Technologies
        </p>
        <div className="flex flex-wrap gap-2">
          {technologies.map((item) => (
            <span
              key={item}
              className="inline-flex rounded-full border border-[#d8deeb] bg-[#f7faff] px-3 py-1.5 text-xs font-medium sm:px-3.5 sm:text-sm"
            >
              {item}
            </span>
          ))}
        </div>
      </div>

      <div className="mt-5">
        <p className="m-0 mb-2 text-[10px] font-semibold uppercase tracking-widest text-(--desaturatedbluecustom) sm:text-xs">
          Service Base URLs
        </p>
        <p className="m-0 mb-3 text-sm leading-normal tracking-[0.0175rem] sm:text-[15px]">
          Service Base URL List information is displayed here if a health IT
          developer has listings certified to (g)(10) and therefore must
          comply with{" "}
          <a
            href={apiMaintenanceOfCertificationUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium underline! text-(--darkblue) decoration-(--darkblue) underline-offset-2 hover:opacity-80"
          >
            API Maintenance of Certification
          </a>{" "}
          requirements. For more information, please visit the{" "}
          <a
            href={apiResourceGuideUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium underline! text-(--darkblue) decoration-(--darkblue) underline-offset-2 hover:opacity-80"
          >
            API Resource Guide
          </a>
          .
        </p>
        <div className="overflow-hidden rounded-xl border border-[#e3e7f3] bg-white">
          <div className="border-b border-[#e3e7f3] bg-[#f7faff] px-3 py-2">
            <p className="m-0 text-xs font-semibold uppercase tracking-widest">
              URL
            </p>
          </div>
          <ul className="m-0 list-none p-0">
            {serviceBaseUrls.map((item) => (
              <li
                key={item.href}
                className="flex items-center gap-2 border-b border-[#e3e7f3] px-3 py-2.5 last:border-b-0"
              >
                <div className="flex min-w-0 flex-1 items-center gap-2.5">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-[#f7faff] text-(--desaturateddarkblue)">
                    <LinkOutlinedIcon className="text-base" />
                  </div>
                  <div className="min-w-0">
                    <p className="m-0 text-[10px] font-semibold uppercase tracking-widest text-(--desaturatedbluecustom)">
                      {item.label}
                    </p>
                    <a
                      href={item.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-0.5 text-(--darkblue) block break-all text-xs font-medium underline! decoration-(--darkblue) underline-offset-2 hover:opacity-80 sm:text-sm"
                    >
                      {item.displayUrl}
                    </a>
                  </div>
                </div>
                <IconButton
                  aria-label={`Open ${item.label}`}
                  href={item.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  size="small"
                  className="shrink-0 rounded-md! p-1! text-(--darkblue)! hover:bg-[#e5ebff]!"
                >
                  <OpenInNewOutlinedIcon className="text-sm" />
                </IconButton>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="mt-5 flex flex-col gap-3 sm:flex-row">
        <Button
          variant="contained"
          disableElevation
          endIcon={<OpenInNewOutlinedIcon className="text-lg" />}
          onClick={handleViewDocumentation}
          className="normal-case! w-full rounded-xl! bg-(--darkblue)! px-5! py-3! text-sm! font-semibold! text-white! hover:bg-(--darkbluehomecustom)! sm:w-auto"
        >
          View Documentation
        </Button>
        <Button
          variant="outlined"
          endIcon={<DownloadOutlinedIcon className="text-lg" />}
          onClick={handleDownloadPdf}
          className="normal-case! w-full rounded-xl! border-[rgba(4,30,121,0.35)]! px-5! py-3! text-sm! font-semibold! text-(--darkblue)! hover:border-(--darkblue)! hover:bg-[#e5ebff]! sm:w-auto"
        >
          Download PDF
        </Button>
      </div>
    </section>
  );
};

export default DeveloperGuideMetadata;
